import type { JSX } from "react";
import type { Notification, NotificationSeverity } from "../../../services/notification/notification-service";
import type { IconComponent } from "../../atoms/icons/icon-component";
import type { SnackbarOrigin } from "@mui/material";
import type { TransitionProps } from "@mui/material/transitions";
import { Slide, Snackbar, Typography, css, styled } from "@mui/material";
import classNames from "classnames";
import { useObservable } from "ecce-react";
import { VisuallyHidden } from "@charterindex/armoury-www";
import { SuccessIcon, WarningIcon, ErrorIcon, InfoIcon } from "../../atoms/icons/mui-icons";


const NotificationRoot = styled(Snackbar)(({ theme }) => css`
	padding-top: ${theme.layout.navBarHeight};

	.notification {
		display: grid;
		grid-template-areas: 
			'icon title action'
			'.    message message'
		;
		column-gap: ${theme.spacing(.75)};
		padding: ${theme.spacing(1, 1, 1, 1)};
		border-radius: ${theme.shape.borderRadius}px;

		box-shadow: ${theme.shadows[10]};
		
		&.success {
			background-color: ${theme.palette.success.main};
			color: ${theme.palette.success.contrastText};
		}
		&.info {
			background-color: ${theme.palette.primary.main};
			color: ${theme.palette.primary.contrastText};
		}
		&.warning {
			background-color: ${theme.palette.warning.main};
			color: ${theme.palette.warning.contrastText};
		}
		&.error {
			background-color: ${theme.palette.error.main};
			color: ${theme.palette.error.contrastText};
		}

		&__icon {
			grid-area: icon;
			margin: auto 0;
			font-size: 2em;
		}
		&__title {
			grid-area: title;
			margin: auto 0;
		}
		&__action {
			grid-area: action;
		}
		&__message{
			grid-area: message;
		}
	}
`);

const DEFAULT_ICON: Readonly<Record<NotificationSeverity, IconComponent>> = {
	info: InfoIcon,
	error: ErrorIcon,
	success: SuccessIcon,
	warning: WarningIcon,
};

const ORIGIN: SnackbarOrigin = { vertical: "bottom", horizontal: "right" };
const TRANSITION_PROPS = { direction: "left" } as TransitionProps;

export type NotificationViewProps = {
	notification: Notification;
};
export const NotificationView = ({ notification }: NotificationViewProps): JSX.Element => {
	useObservable(notification, "open");

	const Icon = notification.icon ?? DEFAULT_ICON[notification.severity];

	return (
		<NotificationRoot
			key={ notification.id }
			open={ notification.open }
			anchorOrigin={ ORIGIN }
			slots={{
				transition: Slide,
			}}
			slotProps={{
				transition: TRANSITION_PROPS,
			}}
		>
			<div className={ classNames("notification", notification.severity) }>
				<Icon
					className="notification__icon"
					aria-hidden
				/>
				<VisuallyHidden text={ notification.severity }/>
				<Typography
					component="span"
					className="notification__title"
					sx={{ fontWeight: 500, fontSize: "1.1rem" }}
				>
					{ notification.title }
				</Typography>
				{ notification.message && (
					<Typography
						component="div"
						className="notification__message"
						sx={{ paddingInlineEnd: 1 }}
					>
						{ notification.message }
					</Typography>
				) }
			</div>
		</NotificationRoot>
	);
};
